import React from 'react';
import { useApp } from '@/contexts/AppContext';
import ActionMenu from '@/components/apex/ActionMenu';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import AnimatedCounter from '@/components/apex/AnimatedCounter';
import RecordDrawer from '@/components/apex/RecordDrawer';
import useListDrawer from '@/hooks/useListDrawer';

const PRIO_COLOR = {
  critical: 'border-l-apex-critical',
  high: 'border-l-apex-warning',
  medium: 'border-l-apex-lens',
  low: 'border-l-slate-300',
};

export default function Remediation() {
  const { seed } = useApp();
  const navigate = useNavigate();

  // Flatten groups → single ordered list for keyboard nav + deep links
  const flatActions = (seed?.remediation_groups ?? []).flatMap((g) =>
    g.actions.map((a) => ({ ...a, _priority: g.priority })),
  );
  const { active: activeItem, setActive, openPrev, openNext, index, total } = useListDrawer({ items: flatActions });
  if (!seed) return null;

  const r = seed.remediation_impact;

  const buildRecord = (a) => {
    const debtK = a.debt_usd / 1000;
    const tone = a._priority === 'critical' ? 'critical' : a._priority === 'high' ? 'warning' : 'improving';
    return {
      idTag: a.id,
      title: a.title,
      severity: a._priority,
      subtitle: 'Remediation action · APEX-generated, scoped to the smallest reversible change',
      verdict: {
        tone,
        label: tone === 'critical' ? 'Closes a Tier-0 exposure' : tone === 'warning' ? 'Closes an audit-material gap' : 'Reduces governance debt',
        sub: `Estimated ${a.effort_minutes} minutes of focused work removes $${debtK.toFixed(0)}K of debt and closes the underlying control finding when CloudTrail confirms the change.`,
      },
      signal: {
        tone: 'improving',
        lead: 'Outcome on close:',
        body: 'APEX auto-marks this action complete the moment the next evidence cycle confirms the resource matches the approved baseline. The audit packet inherits the closure timestamp and the change of custody trail without any manual upload.',
      },
      meta: [
        { label: 'Effort budget', value: `${a.effort_minutes}m`, mono: true, sub: 'Smallest reversible change' },
        { label: 'Debt reduction', value: `−$${debtK.toFixed(0)}K`, mono: true, tone: 'success', sub: 'Calculated from exposure × sensitivity', delta: 'down' },
        { label: 'Due', value: a.due, mono: true, sub: 'Per remediation calendar' },
        { label: 'Linked investigation', value: a.investigation ?? 'Not linked', mono: true, tone: a.investigation ? 'critical' : '', sub: a.investigation ? 'Closes one contributor · click to open' : 'Standalone fix', pivot: a.investigation ? { to: `/lens/investigations?id=${a.investigation}`, label: `Open ${a.investigation}` } : undefined },
      ],
      sections: [
        {
          title: 'Debt burn-down · current quarter',
          type: 'chart',
          chart: {
            kind: 'burndown',
            yLabel: 'Open governance debt ($K)',
            labels: ['Q-start', 'W-9', 'W-7', 'W-5', 'W-3', 'W-1', 'Today'],
            actual: [1100, 1042, 980, 921, 880, 853, 847],
            ideal:  [1100, 1010, 920, 830, 740, 650, 600],
          },
        },
        {
          title: 'Operational signals',
          type: 'kpis',
          kpis: [
            { label: 'Time-to-close', value: `${a.effort_minutes}m`, sub: 'Engineering effort budgeted', tone: 'lens', delta: 'flat' },
            { label: 'Audit weight', value: a._priority === 'critical' ? 'Material' : 'Routine', sub: a._priority === 'critical' ? 'Surfaces in next audit pull' : 'Standard quarterly evidence', tone: a._priority === 'critical' ? 'warning' : 'success', delta: a._priority === 'critical' ? 'up' : 'flat' },
            { label: 'Confidence', value: 'High', sub: 'Reversible change with rollback path', tone: 'success', delta: 'flat' },
          ],
        },
        {
          title: 'Steps',
          type: 'list',
          items: [
            'Open the linked investigation and confirm scope is unchanged from when this action was generated.',
            'Apply the change via the documented pipeline (PR + reviewer) — no console actions.',
            'Verify the next CloudTrail evidence cycle confirms the gap is closed.',
            'APEX auto-marks this action complete when verification arrives — no manual upload.',
          ],
        },
        {
          title: 'What this fixes',
          type: 'paragraph',
          body: `Closing this action removes one of the contributors to your governance debt. The estimated reduction is $${debtK.toFixed(0)}K — calculated from the current exposure window and the data sensitivity of the resources involved.`,
        },
        {
          title: 'Impact of closing this action',
          type: 'kpis',
          kpis: [
            { label: 'Governance debt reduction', sub: 'Calculated from exposure × sensitivity × duration', value: `−$${debtK.toFixed(0)}K`, tone: 'success', delta: 'down' },
            { label: 'Evidence Readiness impact', sub: 'Estimated improvement to the header metric', value: `+${Math.max(1, Math.round(a.debt_usd / 25000))}%`, tone: 'lens', delta: 'up' },
            { label: 'Effort required', sub: 'Smallest reversible change · no downtime', value: `${a.effort_minutes}m`, tone: 'neutral', delta: 'flat' },
            { label: 'Auto-close on next evidence cycle', sub: 'APEX marks complete when CloudTrail confirms the change', value: 'Yes', tone: 'success', delta: 'flat' },
          ],
        },
      ],
      actions: [
        ...(a.investigation ? [{ label: `Open ${a.investigation}`, onClick: () => navigate(`/lens/investigations?id=${a.investigation}`), primary: true }] : []),
        { label: 'Mark complete', onClick: () => navigate('/lens/audit') },
      ],
    };
  };

  return (
    <div className="space-y-6" data-testid="remediation">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Governance · Remediation Center</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">From proof to fix</h1>
        <p className="text-sm text-apex-neutral mt-1">{r.actions_total} actions assigned · {r.hours_estimated} hours estimated · ${(r.debt_reduction_usd / 1000).toFixed(0)}K debt reduction when complete</p>
      </div>

      {/* Groups */}
      <div className="space-y-5">
        {seed.remediation_groups.map((g) => (
          <div key={g.team} data-testid={`remediation-${g.team.replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-apex-ink">{g.team} <span className="text-apex-neutral font-normal">· {g.actions.length} actions</span></h3>
              <span className={`text-[10px] uppercase tracking-wider font-semibold ${g.priority === 'critical' ? 'text-apex-critical' : g.priority === 'high' ? 'text-apex-warning' : 'text-apex-lens'}`}>
                {g.priority} priority
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {g.actions.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  data-testid={`rm-card-${a.id}`}
                  onClick={() => setActive(flatActions.find((x) => x.id === a.id))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(flatActions.find((x) => x.id === a.id)); } }}
                  className={`bg-white rounded-lg border-l-4 ${PRIO_COLOR[g.priority]} border border-slate-200 p-4 cursor-pointer hover:shadow-sm hover:-translate-y-0.5 transition-all`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <span className="text-[10px] font-mono text-apex-neutral">{a.id}</span>
                      <h4 className="text-sm font-semibold text-apex-ink leading-snug mt-0.5">{a.title}</h4>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <ActionMenu context={a.id} testId={`rm-actions-${a.id}`} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-apex-neutral font-mono">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{a.effort_minutes} min</span>
                    <span>·</span>
                    <span className="text-apex-success">−${(a.debt_usd / 1000).toFixed(0)}K</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />Due {a.due}</span>
                  </div>
                  {a.investigation && (
                    <button onClick={(e) => { e.stopPropagation(); navigate(`/lens/investigations?id=${a.investigation}`); }} className="mt-2 text-[11px] text-apex-lens font-semibold inline-flex items-center gap-1">
                      {a.investigation} <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Impact preview */}
      <div className="bg-apex-shell text-white rounded-xl p-5 grain">
        <h3 className="text-sm font-bold mb-4">If all {r.actions_total} actions complete</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Impact label="Governance Debt" before={`$${(r.before.debt_usd / 1000).toFixed(0)}K`} after={`$${(r.after.debt_usd / 1000).toFixed(0)}K`} good="lower" />
          <Impact label="Evidence Readiness" before={`${r.before.evidence_readiness}%`} after={`${r.after.evidence_readiness}%`} good="higher" />
          <Impact label="Production Contamination" before={r.before.contamination} after={r.after.contamination} good="lower" />
        </div>
      </div>

      <RecordDrawer
        open={!!activeItem}
        onOpenChange={(o) => !o && setActive(null)}
        record={activeItem ? buildRecord(activeItem) : null}
        testId="rm-drawer"
        onPrev={openPrev}
        onNext={openNext}
        position={index}
        total={total}
      />
    </div>
  );
}

function Impact({ label, before, after, good }) {
  return (
    <div className="rounded-lg bg-slate-900/60 border border-slate-800 p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-sm text-slate-500 line-through tabular">{before}</span>
        <span className="text-slate-500">→</span>
        <span className="text-2xl font-bold text-apex-lens tabular">{after}</span>
      </div>
    </div>
  );
}
